/**
 * Signup/login funnel analytics for the new auth experience (CURI-334).
 *
 * Events are tracked via the Mixpanel logger (useMetrics(true) → APP.<event>) so
 * they fire before the user is authenticated. The funnel is:
 *   Auth.Started → Auth.MethodSelected → [magic link: CodeSent → CodeSubmitted]
 *   → Auth.Succeeded
 * Signups = Auth.Succeeded where isNewUser; signup-via-login = Auth.Succeeded
 * where intendedMode === 'login' && isNewUser.
 *
 * Success is detected app-wide (BackendServiceProvider) by pairing this `pending`
 * marker — set at initiation and surviving the OAuth redirect — with isNewUser
 * from the resolved session, so it works across every flow and catches a login
 * click that ends up creating an account.
 */

export const AUTH_EVENTS = {
  started: 'Auth.Started',
  methodSelected: 'Auth.MethodSelected',
  codeSent: 'Auth.MagicLinkCodeSent',
  codeSubmitted: 'Auth.MagicLinkCodeSubmitted',
  codeResent: 'Auth.MagicLinkCodeResent',
  codeFailed: 'Auth.MagicLinkCodeFailed',
  sendFailed: 'Auth.MagicLinkSendFailed',
  succeeded: 'Auth.Succeeded',
  // Student join-code (PIN) flow from the auth screen.
  joinStarted: 'Auth.JoinCodeStarted',
  joinRedirected: 'Auth.JoinCodeRedirected',
} as const;

export type AuthFunnelMode = 'login' | 'signup';
export type AuthFunnelMethod = 'oauth' | 'magicLink';

export type AuthPending = {
  mode: AuthFunnelMode;
  method: AuthFunnelMethod;
  provider?: string;
};

const PENDING_KEY = 'curipod_auth_pending';

// Matches the curipod_auth_flow cookie max-age: long enough to cover the OAuth
// round-trip, short enough that an abandoned/failed/cancelled attempt's marker
// expires instead of mislabelling a much later success.
const PENDING_TTL_MS = 10 * 60 * 1000;

type StoredPending = AuthPending & { ts: number };

/** Remember an in-flight auth attempt so Auth.Succeeded can fire after the
 *  redirect, carrying the intended mode/method. Safe to call on the client only. */
export function setAuthPending(pending: AuthPending): void {
  try {
    const stored: StoredPending = { ...pending, ts: Date.now() };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(stored));
  } catch {
    // sessionStorage unavailable (private mode / SSR) — funnel success tracking
    // degrades gracefully; the auth flow itself is unaffected.
  }
}

export function readAuthPending(): AuthPending | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const { ts, ...pending } = JSON.parse(raw) as StoredPending;
    // Stale marker (attempt was abandoned, failed or cancelled) — drop it so a
    // later, unrelated success isn't logged with the wrong method/provider.
    if (!ts || Date.now() - ts > PENDING_TTL_MS) {
      clearAuthPending();
      return null;
    }
    return pending;
  } catch {
    return null;
  }
}

export function clearAuthPending(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}
